/**
 * net/clock.ts — o relógio do receptor, e a correção de relógio D6.
 *
 * POR QUE ISTO EXISTE COMO ARQUIVO: o receptor tem que tomar duas decisões com o
 * tempo — "qual item toca agora" e "este anúncio ainda vale?" — e as duas
 * NÃO podem ler `Date.now()` direto, porque (a) no critério 7 do
 * PLAYER.md §9 o teste precisa de 22:00 sem esperar até 22:00, e (b) a TV Box
 * chega com relógio errado (PLAYER.md §4.4).
 *
 * D6 (contrato-base §9): se `|serverTime − now| > 120 s`, aplica o offset.
 * O offset é SOMADO ao relógio local, nunca o substitui — se a TV ganha 4 min por
 * dia, corrigir o valor uma vez só faria ela voltar a errar em uma semana.
 *
 * ponytail: sem dependência de data (o `luxon` do gestor não vem para cá). O
 * domínio compartilhado já é a fonte de verdade do "quando exibir"; isto é só a
 * fonte de verdade do "que horas são AGORA".
 */

/** Acima disto, o relógio local é considerado ERRADO e não "um pouco atrás". */
export const CLOCK_SKEW_THRESHOLD_MS = 120_000;

/** Fonte de tempo injetável. `now()` em milissegundos desde a época Unix. */
export interface Clock {
  /** Relógio LOCAL e cru, sem correção (é o que o backend não deve ver). */
  now(): number;
}

export const systemClock: Clock = {
  now: () => Date.now(),
};

/**
 * Relógio do receptor: embrulha um `Clock` local e guarda o offset do servidor.
 *
 * `now()` é o tempo JÁ CORRIGIDO — é o que a reavaliação de agendamento e o
 * player usam. `rawNow()` é o local sem offset, para telemetria honesta.
 */
export class SyncedClock implements Clock {
  private readonly local: Clock;
  private offsetMs = 0;
  private lastSyncAt: number | null = null;

  constructor(local: Clock = systemClock) {
    this.local = local;
  }

  now(): number {
    return this.local.now() + this.offsetMs;
  }

  nowDate(): Date {
    return new Date(this.now());
  }

  rawNow(): number {
    return this.local.now();
  }

  /** Offset aplicado, em ms. 0 = nunca corrigido. */
  get offset(): number {
    return this.offsetMs;
  }

  /** Quando foi a última sincronização aceita (ms local). `null` = nunca. */
  get lastSync(): number | null {
    return this.lastSyncAt;
  }

  /**
   * Aplica um `serverTime` do backend.
   *
   * Devolve `true` se o offset MUDOU. A regra do limiar (120 s) mora aqui e só
   * aqui: abaixo dele, ajustar seria trocar um desvio irrelevante por outro
   * (jitter de rede virando jitter de agenda — "o anúncio some 1 s mais cedo").
   *
   * `serverTime` inválido/ausente é ignorado em silêncio: um payload de heartbeat
   * com campo quebrado NÃO pode zerar a hora de uma TV que estava certa.
   */
  sync(serverTime: string | number | undefined | null): boolean {
    const serverMs = typeof serverTime === 'number' ? serverTime : Date.parse(String(serverTime ?? ''));
    if (!Number.isFinite(serverMs)) {
      return false;
    }
    const delta = serverMs - this.local.now();
    if (Math.abs(delta) <= CLOCK_SKEW_THRESHOLD_MS) {
      // Dentro da tolerância: NÃO mexe no offset já aplicado (evita Tremor).
      this.lastSyncAt = this.local.now();
      return false;
    }
    this.offsetMs = delta;
    this.lastSyncAt = this.local.now();
    return true;
  }

  /** Zera a correção — usado quando a credencial é apagada (tela revogada). */
  reset(): void {
    this.offsetMs = 0;
    this.lastSyncAt = null;
  }
}

/**
 * Cronômetro com `sleep` injetável. Junto com o `Clock`, é o que torna os
 * critérios 6 (30 min offline) e 7 (expira às 22:00) testáveis em milissegundos.
 */
export interface Sleeper {
  sleep(ms: number, signal?: AbortSignal): Promise<void>;
}

export const systemSleeper: Sleeper = {
  sleep: (ms, signal) =>
    new Promise<void>((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      const onAbort = (): void => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      };
      const timer = setTimeout(() => {
        signal?.removeEventListener('abort', onAbort);
        resolve();
      }, ms);
      signal?.addEventListener('abort', onAbort, { once: true });
    }),
};

/**
 * Relógio controlado por TESTE: o tempo só anda quando o teste manda.
 * `advance` é a peça que transforma "30 minutos offline" num teste de 3 ms.
 */
export class FakeClock implements Clock, Sleeper {
  private current: number;
  private readonly pending: { at: number; resolve: () => void; reject: (e: Error) => void; signal?: AbortSignal }[] = [];

  constructor(start: number | string = 0) {
    this.current = typeof start === 'string' ? Date.parse(start) : start;
  }

  now(): number {
    return this.current;
  }

  nowDate(): Date {
    return new Date(this.current);
  }

  /** Anda o relógio. Não dispara nada sozinho — quem dispara é `runDue`. */
  advance(ms: number): void {
    this.current += ms;
    this.runDue();
  }

  /** Pula direto para um instante absoluto (ISO ou ms). */
  setTo(when: number | string): void {
    this.current = typeof when === 'string' ? Date.parse(when) : when;
    this.runDue();
  }

  sleep(ms: number, signal?: AbortSignal): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      this.pending.push({ at: this.current + ms, resolve, reject, ...(signal ? { signal } : {}) });
    });
  }

  /** Resolve todas as esperas cujo prazo chegou. `advance` chama isto sozinho. */
  runDue(): void {
    const rest: typeof this.pending = [];
    for (const entry of this.pending) {
      if (entry.at <= this.current) {
        if (entry.signal?.aborted) {
          entry.reject(new DOMException('Aborted', 'AbortError'));
        } else {
          entry.resolve();
        }
      } else {
        rest.push(entry);
      }
    }
    this.pending.length = 0;
    this.pending.push(...rest);
  }

  /** Quantas esperas estão pendentes — diagnóstico para teste que trava. */
  get pendingCount(): number {
    return this.pending.length;
  }
}
